import { useEffect, useRef } from "react";
import { WhatsAppMessage } from "../types/api";

const API_URL = process.env.REACT_APP_API_URL || "https://dev-backend.taliaz-cm.com";

interface UseWhatsAppSSEOptions {
  onNewMessage?: (message: WhatsAppMessage) => void;
  onStatusUpdate?: (message: WhatsAppMessage) => void;
  onConversationStatusChanged?: (status: string) => void;
  onCallPermission?: (status: "accepted" | "rejected") => void;
}

export function useWhatsAppSSE(
  phoneNumber: string | undefined,
  isAuthenticated: boolean,
  options: UseWhatsAppSSEOptions = {}
) {
  const { onNewMessage, onStatusUpdate, onConversationStatusChanged, onCallPermission } = options;
  const esRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onNewMessageRef = useRef(onNewMessage);
  const onStatusUpdateRef = useRef(onStatusUpdate);
  const onConvStatusRef = useRef(onConversationStatusChanged);
  const onCallPermissionRef = useRef(onCallPermission);

  onNewMessageRef.current = onNewMessage;
  onStatusUpdateRef.current = onStatusUpdate;
  onConvStatusRef.current = onConversationStatusChanged;
  onCallPermissionRef.current = onCallPermission;

  useEffect(() => {
    if (!isAuthenticated || !phoneNumber) {
      return cleanup;
    }

    connect();
    return cleanup;

    function connect() {
      const token = localStorage.getItem("token");
      if (!token) return;

      const url = `${API_URL}/whatsapp/events?token=${token}&phone_number=${encodeURIComponent(phoneNumber!)}`;
      const es = new EventSource(url);
      esRef.current = es;

      es.onmessage = (e) => {
        try {
          const event = JSON.parse(e.data);
          if (event.type === "new_message") {
            onNewMessageRef.current?.(event.message);
          } else if (event.type === "status_update") {
            onStatusUpdateRef.current?.(event.message);
          } else if (event.type === "conversation_status_changed") {
            onConvStatusRef.current?.(event.status);
          } else if (event.type === "call_permission") {
            onCallPermissionRef.current?.(event.status);
          }
        } catch {
          console.error("[WhatsAppSSE] Failed to parse event:", e.data);
        }
      };

      es.onerror = () => {
        es.close();
        esRef.current = null;
        reconnectTimerRef.current = setTimeout(connect, 5000);
      };

      console.log("[WhatsAppSSE] Connection opened for phone", phoneNumber);
    }

    function cleanup() {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
        console.log("[WhatsAppSSE] Connection closed");
      }
    }
  }, [isAuthenticated, phoneNumber]); // eslint-disable-line react-hooks/exhaustive-deps
}
